import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Eye } from "lucide-react";
import { toast } from "sonner";
import { CreateModal, Editor } from "@/components/legal/Oficios";

const STATUS_META = {
  borrador: { label: "Borrador", color: "#94a3b8" },
  revision: { label: "En revisión", color: "#22d3ee" },
  observado: { label: "Observado", color: "#fbbf24" },
  rechazado: { label: "Rechazado", color: "#f87171" },
  autorizado: { label: "Autorizado", color: "#4ade80" },
  emitido: { label: "Emitido", color: "#60a5fa" },
  cancelado: { label: "Cancelado", color: "#64748b" },
  sustituido: { label: "Sustituido", color: "#a78bfa" },
};
const DELIVERY_META = {
  entregado_sin_acuse: { label: "Entregado sin acuse", color: "#38bdf8" },
  acusado: { label: "Acusado", color: "#34d399" },
};

export default function ContractOficios({ contract }) {
  const { user } = useAuth();
  const canAuthorize = ["admin", "contador"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/oficios", { params: { contract_id: contract.id } })
    .then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/oficios/templates").then((r) => setTemplates(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [contract.id]);

  const openOficio = async (id) => {
    try { const { data } = await api.get(`/oficios/${id}`); setEditing(data); }
    catch { toast.error("No se pudo abrir el oficio"); }
  };

  return (
    <div className="panel p-4 space-y-3" data-testid="contract-oficios">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono">// OFICIOS DEL CONTRATO (variables preligadas)</div>
        <button data-testid="contract-oficio-new-btn" onClick={() => setCreating(true)}
          className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Generar Oficio
        </button>
      </div>
      {items.length === 0 && <div className="text-xs text-slate-500 italic">Sin oficios para este contrato. Genera el primero — los datos del contrato se completan automáticamente.</div>}
      <div className="space-y-1.5">
        {items.map((o) => {
          const m = STATUS_META[o.status] || { label: o.status, color: "#94a3b8" };
          const dm = DELIVERY_META[o.delivery_status];
          return (
            <div key={o.id} className="flex items-center gap-2 p-2 rounded-sm cursor-pointer hover:bg-slate-800/40"
              style={{ background: "rgba(15,23,42,0.5)" }} onClick={() => openOficio(o.id)} data-testid={`contract-oficio-${o.id}`}>
              <span className="font-mono text-xs text-cyan-300 w-40 shrink-0 truncate">{o.folio || "— sin folio —"}</span>
              <span className="text-sm text-slate-200 flex-1 truncate">{o.subject}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase shrink-0" style={{ color: m.color, background: `${m.color}1a` }}>{m.label}</span>
              {o.status === "emitido" && (
                <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase shrink-0"
                  style={{ color: (dm || { color: "#94a3b8" }).color, background: `${(dm || { color: "#94a3b8" }).color}1a` }}
                  data-testid={`contract-oficio-delivery-${o.id}`}>
                  {dm ? dm.label : "Pendiente de entrega"}
                </span>
              )}
              <span className="font-mono text-[10px] text-slate-500 shrink-0">{fmtDate(o.created_at)}</span>
              <Eye className="w-4 h-4 text-slate-500 shrink-0" />
            </div>
          );
        })}
      </div>
      {creating && (
        <CreateModal templates={templates} contracts={[]} fixedContract={contract}
          onClose={() => setCreating(false)}
          onCreated={(d) => { setCreating(false); setEditing(d); load(); }} />
      )}
      {editing && (
        <Editor oficio={editing} canAuthorize={canAuthorize}
          onClose={() => { setEditing(null); load(); }} onChanged={() => load()} />
      )}
    </div>
  );
}
